/**
 * Cart item rehydration utilities.
 *
 * When a quotation is reopened for editing, its stored `SaleItem[]` (a frozen,
 * denormalized snapshot) must be turned back into `CartItem[]` so the POS cart
 * can recompute totals and let the user edit lines. The reconstruction is
 * intentionally lossy: it only restores the fields the cart math and display
 * need (price, quantity, per-item discount, tax rate, variant/location, type
 * flags). Adding brand-new lines still goes through the normal product search.
 */

import { CartItem, SaleItem } from '../../core/models/sale.model';
import { Unit } from './unit-format.utils';

/**
 * What one unit of a sold line actually cost the customer.
 *
 * `SaleItem.price` is the unit price *before* the line's own discount, so a line
 * sold at $5.250 with $500 off two units was paid at $5.000 a unit, not $5.250.
 * Anything that hands money back has to start from this figure — refunding the
 * undiscounted one pays out more than came in, which is the same over-refund
 * that made a Price List sale refund at catalogue (PL-12, AC 4).
 *
 * Dividing rather than carrying the discount across is what makes a **partial**
 * return come out right: the discount was agreed for the whole line, so one unit
 * of three is owed a third of it, and folding it into the unit price prorates it
 * with no second field to keep in step (and no sign rule for a negative line).
 *
 * @returns The unit price paid. Left unrounded: the cart rounds once, at the
 *   total (`calculateTotalsImmutable`), so rounding here would lose a peso per
 *   unit instead.
 */
export function soldUnitPrice(item: SaleItem): number {
  const price = Number(item.price) || 0;
  const quantity = Number(item.quantity) || 0;
  const discount = Number(item.discount) || 0;
  if (!discount || quantity === 0) return price;
  return (price * quantity - discount) / quantity;
}

/**
 * Rebuild a single editable cart line from a stored quotation line.
 *
 * @param item - The frozen quotation line item
 * @param pricesStillPromised - Whether the document's prices are still owed to
 *   the customer. `true` for a quotation inside its validity, whose figures no
 *   list may move. `false` once that validity has lapsed (see
 *   {@link quoteIsExpired}): the line comes back unfrozen so today's rules can
 *   price it — but note that `basePrice` below is still the *charged* figure,
 *   so the caller must re-seed the catalogue fields from the product before
 *   repricing, or the engine will discount an already discounted number.
 * @returns A CartItem carrying the frozen price/quantity/discount/tax so the
 *   cart can recompute subtotal/tax/total without re-reading the catalog.
 */
export function saleItemToCartItem(item: SaleItem, pricesStillPromised = true): CartItem {
  const price = Number(item.price) || 0;
  const quantity = Number(item.quantity) || 0;

  return {
    // Product identity (lossy: only what the cart needs)
    id: item.productId,
    businessId: '',
    active: true,
    name: item.productName,
    reference: item.productRef ?? '',
    categoryId: item.categoryId ?? '',
    categoryName: item.categoryName,
    basePrice: price,
    baseCost: item.cost,
    taxRate: item.taxRate,
    commissionRate: item.commissionRate,
    unit: (item.unit as Unit) ?? Unit.PIECE,
    hasVariants: !!item.variantId,
    isService: item.isService ?? false,
    isCombo: item.isCombo ?? false,
    isPresentation: item.isPresentation ?? false,
    baseProductId: item.baseProductId,
    baseVariantId: item.baseVariantId,
    presentationUnit: item.presentationUnit,
    comboComponents: item.comboComponents,

    // Cart line state
    quantity,
    itemTotal: price * quantity,
    discountAmount: Number(item.discount) || 0,
    price,

    // The list this line was charged through, kept for the sale it becomes.
    priceListId: item.priceListId,
    priceListName: item.priceListName,
    /**
     * A quotation's prices are a promise already made to the customer, so no
     * list moves them — a Quote converted late is governed by the *quote's* own
     * validity (CONTEXT.md, "Active price list"). It is also all we have:
     * `basePrice` above is the price that was charged, not the catalogue one,
     * so re-applying an adjustment would discount an already discounted number.
     *
     * Frozen, never "typed by hand": nobody typed these at this till, and the
     * offer to hand the line back to the list would double-discount it.
     *
     * Once that validity lapses the promise is over and the freeze comes off —
     * see `pricesStillPromised`. The caller re-seeds the catalogue fields first,
     * for the reason the paragraph above gives.
     */
    priceFrozen: pricesStillPromised,

    // Variant / location
    variantId: item.variantId,
    variantName: item.variantName,
    variantAttributes: item.variantAttributes,
    locationId: item.locationId,
    locationName: item.locationName,
  };
}
